<script setup lang="ts">
import SvgIcon from '@telecom/shared/components/SvgIcon.vue';
import IconEdit from '@patsnap-ui/icon/assets/solid/edit.svg';
import IconRegenerate from '@/presentation/assets/images/icons/icon-regenerate.svg';
import IconCopilot from '@/presentation/assets/images/icons/icon-copilot.svg';
import FeedBack from '@/presentation/components/feedback/FeedBack.vue';
import EmbodimentToggle from './EmbodimentToggle.vue';
import EmbodimentConfig from './EmbodimentConfig.vue';
import EmbodimentContent from './EmbodimentContent.vue';
import { useI18n } from 'vue-i18n';
import { ref, onMounted } from 'vue';
import { useClaimFeatures } from './composables/use-claim-features';
import { useEmbodiment } from './composables/use-embodiment';
import { useEmbodimentRules } from './composables/use-embodiment-rules';
import { useSpecificationTrack } from '@/presentation/pages/specification/composables/use-specification-track';
import * as TrackEvents from '@/constants/track-events';
import { SpecificationContentType } from '@/domains/specification/enums';

const emits = defineEmits<{
    (e: 'addition', value: string): void;
    (e: 'cancel'): void;
    (e: 'close'): void;
}>();

const { t } = useI18n();
const { isLoading, fetchClaimFeatures } = useClaimFeatures();
const { updateRules, submitRule, disabledSubmitRule } = useEmbodimentRules();
const {
    embodiment,
    isGeneratingEmbodiment,
    generateEmbodiment,
    restEmbodiment,
} = useEmbodiment();

const { rvTrack } = useSpecificationTrack();

const isEditRuleMode = ref(true);

function openEditRuleMode() {
    isEditRuleMode.value = true;
}

function closeEditRuleMode() {
    isEditRuleMode.value = false;
}

// 编辑规则
function handleEditRule() {
    updateRules(embodiment.value.rules);
    openEditRuleMode();
}

// 提交规则
function handleSubmitRule() {
    submitRule();
    closeEditRuleMode();
    generateEmbodiment();
}

// 关闭弹窗
function handleClose() {
    emits('close');
}

// 提交
function handleSubmit() {
    emits('addition', embodiment.value.content.trim());
}

onMounted(async () => {
    rvTrack(TrackEvents.TRACK_EVENT_SPECIFICATION_GENERATION_EXPAND, {
        eventDetailStr: {
            contentType: SpecificationContentType.Embodiment,
        },
    });
    restEmbodiment();
    await fetchClaimFeatures();
});
</script>

<template>
    <div class="embodiment-generator flex flex-col overflow-hidden max-h-[368px]">
        <!-- 头部区域: 包含标题和重新生成按钮 -->
        <div class="embodiment-generator__header flex items-center justify-between flex-shrink-0">
            <div class="flex items-center">
                <!-- 实施例切换器，仅在非编辑模式显示 -->
                <EmbodimentToggle
                    v-if="!isEditRuleMode"
                    :disabled="isGeneratingEmbodiment"
                />
                <SvgIcon
                    class="th-btn-icon h-6 w-6 fill-gray-450"
                    :icon="IconEdit"
                />
                <span class="ml-1">{{ t('aiSpecification.embodiment.generateEmbodiment') }}</span>
            </div>

            <!-- 重新生成按钮，仅在非编辑模式显示 -->
            <button
                v-if="!isEditRuleMode"
                :disabled="isGeneratingEmbodiment || !embodiment.rules.length"
                class="th-btn th-btn-outline pl-2"
                @click="generateEmbodiment"
            >
                <SvgIcon
                    class="th-btn-icon h-6 w-6 fill-gray-450"
                    :icon="IconRegenerate"
                />
                <span>{{ t('aiDisclosureGlobal.globalBtn.regenerate') }}</span>
            </button>
        </div>

        <!-- 主要内容区域 -->
        <div class="embodiment-generator__content my-4 flex flex-col flex-1 overflow-hidden">
            <!-- 内容标题区域 -->
            <div class="embodiment-generator__content-title flex justify-between mb-1 flex-shrink-0">
                <div class="flex items-start">
                    <SvgIcon
                        class="th-btn-icon h-6 w-6 flex-shrink-0"
                        :icon="IconCopilot"
                    />
                    <span class="ml-1 font-semibold leading-6">{{ t('aiSpecification.embodiment.generateTip') }}</span>
                </div>
                <!-- 编辑按钮，仅在非编辑模式显示 -->
                <button
                    v-if="!isEditRuleMode"
                    :disabled="isGeneratingEmbodiment"
                    class="th-btn th-btn-text th-btn-sm flex-shrink-0"
                    @click="handleEditRule"
                >
                    {{ t('aiDisclosureGlobal.globalBtn.edit') }}
                </button>
            </div>
            <!-- 根据模式显示不同的内容组件 -->
            <template v-if="!isLoading">
                <!-- 编辑模式显示配置组件 -->
                <EmbodimentConfig
                    v-show="isEditRuleMode"
                    class="flex-1"
                />
                <!-- 非编辑模式显示内容组件 -->
                <EmbodimentContent
                    v-show="!isEditRuleMode"
                    class="flex-1"
                />
            </template>
            <!-- 加载状态显示 -->
            <div v-else class="h-[120px] flex items-center justify-center">
                <span class="th-loading th-loading-bars th-loading-lg" />
            </div>
        </div>

        <!-- 底部操作区域 -->
        <div class="embodiment-generator__footer flex items-center justify-between">
            <!-- 反馈组件 -->
            <FeedBack class="flex-shrink-0" />

            <!-- 操作按钮组 -->
            <div class="flex-1 flex justify-end">
                <!-- 编辑模式下的按钮 -->
                <template v-if="isEditRuleMode">
                    <button
                        class="th-btn th-btn-secondary"
                        @click="closeEditRuleMode"
                    >
                        {{ t('aiDisclosureGlobal.globalBtn.cancel') }}
                    </button>
                    <button
                        :disabled="disabledSubmitRule"
                        class="th-btn ml-2"
                        @click="handleSubmitRule"
                    >
                        {{ t('drafting.generating') }}
                    </button>
                </template>

                <!-- 非编辑模式下的按钮 -->
                <template v-else>
                    <button
                        class="th-btn th-btn-secondary"
                        @click="handleClose"
                    >
                        {{ t('aiDisclosureGlobal.globalBtn.cancel') }}
                    </button>
                    <button
                        :disabled="!embodiment.content.trim() || isGeneratingEmbodiment"
                        class="th-btn ml-2"
                        @click="handleSubmit"
                    >
                        {{ t('aiDisclosureGlobal.globalBtn.submit') }}
                    </button>
                </template>
            </div>
        </div>
    </div>
</template>
